import { useEffect, useCallback, useRef } from "react";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { AnalyticsFlowEvent } from "@/analytics/types";
import { useToast } from "@/hooks/useToast";
import { CIEvaluationResult } from "@functions/continuousImprovement/continuousImprovementTypes";
import { useCurrentRapidData, useLastDismissedCITimestamps, useDismissCIAlert } from "@/store";
import { useCurrentWorkspace } from "@/hooks/useCurrentWorkspace";
import { projectSelector } from "@rflocal/shared";

/**
 * Toast-based alerts for CI credit issues.
 *
 * Credit alert: warning toast when insufficient_credits, with action buttons.
 */
export function useCIAlerts() {
    const toast = useToast();
    const { track } = useAnalytics();
    const { currentWorkspace: workspace } = useCurrentWorkspace();
    const rapidData = useCurrentRapidData();
    const lastDismissedTimestamps = useLastDismissedCITimestamps();
    const dismissAlert = useDismissCIAlert();

    const rapidDataId = rapidData?.id;
    const lastCIEvaluation = rapidData?.lastCIEvaluation;
    const lastDismissed = rapidDataId ? lastDismissedTimestamps[rapidDataId] : undefined;

    // Check if credit evaluation is new (not dismissed)
    const evalTimestamp =
        lastCIEvaluation?.timestamp?.toMillis?.() ?? lastCIEvaluation?.timestamp ?? 0;

    const showCreditAlert =
        lastCIEvaluation?.result === CIEvaluationResult.INSUFFICIENT_CREDITS &&
        (!lastDismissed || evalTimestamp > lastDismissed);

    const openUsagePage = useCallback(() => {
        const workspaceUrlPath = workspace?.url ? `/${workspace.url}` : "";
        window.open(
            `${projectSelector.appUrl(false, true)}${workspaceUrlPath}/settings/usage`,
            "_blank"
        );
    }, [workspace]);

    // Flag to distinguish programmatic dismiss (navigation) from user-initiated dismiss (X button)
    const isProgrammaticDismissRef = useRef(false);

    const handleCreditDismiss = useCallback(() => {
        // Skip persistence for programmatic dismiss (navigation away)
        if (isProgrammaticDismissRef.current) {
            isProgrammaticDismissRef.current = false;
            return;
        }
        track({ event: AnalyticsFlowEvent.CI_CREDIT_ALERT_DISMISSED, data: {} });
        if (rapidDataId) dismissAlert(rapidDataId);
    }, [rapidDataId, dismissAlert, track]);

    // Dismiss toast (without persisting) when navigating away from the current rapidData
    useEffect(() => {
        if (!rapidDataId) {
            isProgrammaticDismissRef.current = true;
            toast.dismiss("ci-credit-alert");
        }
    }, [rapidDataId, toast]);

    // Credit alert effect
    useEffect(() => {
        if (!showCreditAlert) return;

        track({ event: AnalyticsFlowEvent.CI_CREDIT_ALERT_SHOWN, data: {} });

        const handleAction = (event: AnalyticsFlowEvent) => {
            track({ event, data: {} });
            toast.dismiss("ci-credit-alert");
            openUsagePage();
            if (rapidDataId) dismissAlert(rapidDataId);
        };

        toast.custom({
            level: "warning",
            children: "Model retraining failed. You're out of credits.",
            actions: [
                {
                    label: "Enable Flex Spending",
                    onClick: () =>
                        handleAction(AnalyticsFlowEvent.CI_CREDIT_ALERT_ENABLE_FLEX_CLICKED)
                },
                {
                    label: "Add Credits",
                    onClick: () =>
                        handleAction(AnalyticsFlowEvent.CI_CREDIT_ALERT_ADD_CREDITS_CLICKED),
                    className: "!bg-yellow-300 hover:!bg-yellow-400"
                }
            ],
            toastId: "ci-credit-alert",
            autoClose: false,
            onClose: handleCreditDismiss
        });
    }, [
        showCreditAlert,
        openUsagePage,
        handleCreditDismiss,
        dismissAlert,
        rapidDataId,
        toast,
        track
    ]);
}
